import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { fetchBrands, createBrand, updateBrand, deleteBrand } from "../api/BrandAPIs";
import { colors } from "../constants/colors";

const SkeletonLoader = ({ type }) => {
  if (type === "table") {
    return (
      <div className="animate-pulse">
        <div className="h-8 bg-gray-200 rounded mb-4"></div>
        {[...Array(3)].map((_, index) => (
          <div key={index} className="flex gap-4 border-b py-3">
            <div className="w-12 h-12 bg-gray-200 rounded"></div>
            <div className="flex-1 space-y-2">
              <div className="h-4 bg-gray-200 rounded w-3/4"></div>
              <div className="h-4 bg-gray-200 rounded w-1/2"></div>
            </div>
          </div>
        ))}
      </div>
    );
  }
  return null;
};

const BrandsPage = () => {
  const navigate = useNavigate();
  const [brands, setBrands] = useState([]);
  const [newBrand, setNewBrand] = useState(null);
  const [editingBrand, setEditingBrand] = useState(null);

  const { data: brandData, isLoading: isLoadingBrands, error: errorBrands } = useQuery({
    queryKey: ["brands"],
    queryFn: fetchBrands,
    staleTime: Infinity,
  });

  const isLoading = isLoadingBrands;
  const error = errorBrands;

  useEffect(() => {
    if (brandData) {
      setBrands(
        brandData.map((brand) => ({
          id: brand.brandId,
          name: brand.name,
          imageBase64: brand.imageBase64 || "",
        }))
      );
    }
  }, [brandData]);

  const handleImageChange = (e, setState, state) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setState({
          ...state,
          imageBase64: reader.result.split(",")[1],
        });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleInputChange = (e, setState, state) => {
    const { name, value } = e.target;
    setState({
      ...state,
      [name]: value,
    });
  };

  const handleCreateBrand = async (e) => {
    e.preventDefault();
    try {
      const created = await createBrand(newBrand);
      setBrands([
        ...brands,
        {
          id: created.brandId,
          name: created.name,
          imageBase64: created.imageBase64,
        },
      ]);
      setNewBrand(null);
    } catch (err) {
      console.error("Error creating brand:", err);
    }
  };

  const handleUpdateBrand = async (e) => {
    e.preventDefault();
    try {
      await updateBrand(editingBrand.id, editingBrand);
      const updatedBrands = await fetchBrands();
      setBrands(
        updatedBrands.map((brand) => ({
          id: brand.brandId,
          name: brand.name,
          imageBase64: brand.imageBase64 || "",
        }))
      );
      setEditingBrand(null);
    } catch (err) {
      console.error("Error updating brand:", err);
    }
  };

  const handleDeleteBrand = async (id) => {
    if (window.confirm("هل أنت متأكد أنك تريد حذف هذه العلامة التجارية؟")) {
      try {
        await deleteBrand(id);
        setBrands(brands.filter((brand) => brand.id !== id));
      } catch (err) {
        console.error("Error deleting brand:", err);
      }
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-4" dir="rtl">
      <div className="max-w-7xl mx-auto">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl md:text-3xl font-bold">إدارة العلامات التجارية</h1>
          <button
            onClick={() => navigate("/dashboard")}
            className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
          >
            العودة إلى الداشبورد
          </button>
        </div>
        {error && (
          <div className="bg-red-100 text-red-700 p-4 rounded mb-6">
            فشل في تحميل البيانات. يرجى المحاولة مرة أخرى.
          </div>
        )}
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl md:text-2xl font-semibold">العلامات التجارية ({brands.length})</h2>
          <button
            onClick={() => setNewBrand({ name: "", imageBase64: "" })}
            className="bg-green-500 text-white px-4 py-2 rounded hover:bg-green-600"
          >
            إضافة علامة تجارية جديدة
          </button>
        </div>
        {isLoading ? (
          <SkeletonLoader type="table" />
        ) : brands.length ? (
          <div className="space-y-6">
            {brands.map((brand) => (
              <div key={brand.id} className="bg-white p-4 rounded shadow">
                <div className="flex justify-between items-center mb-4">
                  <div className="flex items-center gap-4">
                    {brand.imageBase64 && (
                      <img
                        src={`data:image/png;base64,${brand.imageBase64}`}
                        alt={brand.name}
                        className="w-16 h-16 object-cover rounded"
                      />
                    )}
                    <h3 className="text-xl font-semibold">{brand.name}</h3>
                    <h3 className="text-xl font-semibold">{brand.id}</h3>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => navigate(`/brands/${brand.id}/types`)}
                      className="bg-blue-500 text-white px-3 py-1 rounded hover:bg-blue-700"
                    >
                      عرض الأنواع
                    </button>
                    <button
                      onClick={() =>
                        setEditingBrand({
                          id: brand.id,
                          name: brand.name,
                          imageBase64: brand.imageBase64,
                        })
                      }
                      className="bg-yellow-500 text-white px-3 py-1 rounded hover:bg-yellow-600"
                    >
                      تعديل
                    </button>
                    <button
                      onClick={() => handleDeleteBrand(brand.id)}
                      className="bg-red-500 text-white px-3 py-1 rounded hover:bg-red-600"
                    >
                      حذف
                    </button>
                  </div>
                </div>
                {editingBrand && editingBrand.id === brand.id && (
                  <form onSubmit={handleUpdateBrand} className="mb-6">
                    <div className="mb-4">
                      <label className="block text-right mb-2">اسم العلامة التجارية</label>
                      <input
                        type="text"
                        name="name"
                        value={editingBrand.name}
                        onChange={(e) => handleInputChange(e, setEditingBrand, editingBrand)}
                        className="w-full p-2 border rounded"
                        required
                      />
                    </div>
                    <div className="mb-4">
                      <label className="block text-right mb-2">الصورة الحالية</label>
                      {editingBrand.imageBase64 && (
                        <img
                          src={`data:image/png;base64,${editingBrand.imageBase64}`}
                          alt="Current Brand Image"
                          className="h-20 object-cover rounded mb-2"
                        />
                      )}
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => handleImageChange(e, setEditingBrand, editingBrand)}
                        className="w-full p-2 border rounded"
                      />
                    </div>
                    <div className="flex justify-end gap-4">
                      <button
                        type="button"
                        onClick={() => setEditingBrand(null)}
                        className="bg-gray-300 text-black py-2 px-4 rounded hover:bg-gray-400"
                      >
                        إلغاء
                      </button>
                      <button
                        type="submit"
                        className="bg-orange-500 text-white py-2 px-4 rounded hover:bg-orange-600"
                        style={{ backgroundColor: colors.orange }}
                      >
                        حفظ
                      </button>
                    </div>
                  </form>
                )}
              </div>
            ))}
          </div>
        ) : (
          <p className="text-center">لا توجد علامات تجارية</p>
        )}
        {newBrand && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white p-6 rounded-lg w-full max-w-md">
              <h2 className="text-xl font-semibold mb-4" dir="rtl">
                إضافة علامة تجارية جديدة
              </h2>
              <form onSubmit={handleCreateBrand} dir="rtl">
                <div className="mb-4">
                  <label className="block text-right mb-2">اسم العلامة التجارية</label>
                  <input
                    type="text"
                    name="name"
                    value={newBrand.name}
                    onChange={(e) => handleInputChange(e, setNewBrand, newBrand)}
                    className="w-full p-2 border rounded"
                    required
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-right mb-2">الصورة</label>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleImageChange(e, setNewBrand, newBrand)}
                    className="w-full p-2 border rounded"
                  />
                </div>
                <div className="flex justify-end gap-4">
                  <button
                    type="button"
                    onClick={() => setNewBrand(null)}
                    className="bg-gray-300 text-black py-2 px-4 rounded hover:bg-gray-400"
                  >
                    إلغاء
                  </button>
                  <button
                    type="submit"
                    className="bg-orange-500 text-white py-2 px-4 rounded hover:bg-orange-600"
                    style={{ backgroundColor: colors.orange }}
                  >
                    إنشاء
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BrandsPage;